// `useOptimisticMutation` (CHAT-PERF): a mutation whose result is on screen before the server
// answers. Chat uses it for sends, reactions, edits, deletes, pins, topic pin/close and read
// markers; Work tasks are meant to adopt it next (status, assignee, title, reorder).
//
// The life of one call:
//   1. `apply(vars)` patches the cache (live/cache-patch.ts helpers, or `setQueryData`), after
//      the queries under `keys(vars)` are cancelled (an in-flight refetch would overwrite the
//      patch with the old row) and snapshotted.
//   2. `mutationFn(vars)` runs.
//   3. Success: `reconcile(data, vars)` writes the server's copy over the optimistic one (e.g.
//      replace the message by its client id); then the `settle(vars)` keys are invalidated in
//      the background, so counts and anything not patched catch up.
//   4. Failure: by default the snapshot is put back (the change visibly undoes) and the error
//      goes to the query client's toast mapper; `onFailure` replaces that, e.g. a send keeps its
//      row and marks it "failed" with a Retry.
//
// Idempotency: retrying must not double-write. Creates carry a client id the server dedupes
// (chat `client_id`, task `client_id`); toggles send the target state, not "flip it".
//
// Usage (Work, sketch):
//   const move = useOptimisticMutation({
//     keys: () => [workKeys.tasks(co)],
//     apply: (v) => patchLists(qc, workKeys.tasks(co), (d) => upsertRow(d, { ...task, status: v.status }, { insert: false })),
//     mutationFn: (v) => api.client.tasks.update({ params, body: { status: v.status } }),
//     reconcile: (t) => patchLists(qc, workKeys.tasks(co), (d) => upsertRow(d, t, { insert: false })),
//     settle: () => [workKeys.board(co, boardId)],
//   });
import {
  type QueryKey,
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { restore, snapshot } from "../live/cache-patch";

export type OptimisticOptions<TVars, TData> = {
  /** The cache keys (prefixes) the patch touches: cancelled and snapshotted first. */
  keys: (vars: TVars) => QueryKey[];
  /** Writes the expected result into the cache. */
  apply: (vars: TVars) => void;
  mutationFn: (vars: TVars) => Promise<TData>;
  /** Writes the server's answer over the optimistic copy. */
  reconcile?: (data: TData, vars: TVars) => void;
  /** Keys to refetch in the background once the server answered (either way). */
  settle?: (vars: TVars) => QueryKey[];
  /**
   * A failure. Default: roll the cache back to the snapshot and let the global error toast
   * speak. Given: called instead of the rollback (`rollback()` does it on demand).
   */
  onFailure?: (error: unknown, vars: TVars, rollback: () => void) => void;
  onSuccess?: (data: TData, vars: TVars) => void;
  /** The mutation's meta (query.ts reads `quietCodes`: problems the caller shows itself). */
  meta?: Record<string, unknown>;
};

type Context = { snap: (readonly [QueryKey, unknown])[] };

export function useOptimisticMutation<TVars, TData = unknown>(
  o: OptimisticOptions<TVars, TData>,
): UseMutationResult<TData, unknown, TVars, Context> {
  const queryClient = useQueryClient();
  return useMutation<TData, unknown, TVars, Context>({
    mutationFn: o.mutationFn,
    ...(o.meta ? { meta: o.meta } : {}),
    // A refetch in flight is cancelled first: cancelling reverts its query to the state before
    // the fetch, which would land on top of a patch made earlier. Only fetching queries wait.
    onMutate: async (vars) => {
      const keys = o.keys(vars);
      await Promise.all(
        keys
          .filter((queryKey) => queryClient.isFetching({ queryKey }) > 0)
          .map((queryKey) => queryClient.cancelQueries({ queryKey })),
      );
      const snap = keys.flatMap((k) => snapshot(queryClient, k));
      o.apply(vars);
      return { snap };
    },
    onSuccess: (data, vars) => {
      o.reconcile?.(data, vars);
      o.onSuccess?.(data, vars);
    },
    onError: (error, vars, ctx) => {
      const rollback = () => {
        if (ctx) restore(queryClient, ctx.snap);
      };
      if (o.onFailure) o.onFailure(error, vars, rollback);
      else rollback();
    },
    onSettled: (_d, _e, vars) => {
      for (const queryKey of o.settle?.(vars) ?? [])
        void queryClient.invalidateQueries({ queryKey });
    },
  });
}
